import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { AttractionsDirectory } from '../components/AttractionsDirectory';
import { EasyWayPromo } from '../components/EasyWayPromo';
import { ATTRACTIONS } from '../data/attractions';
import { Attraction, Category } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useSEO } from '../hooks/useSEO';
import { getLocalizedRoute, ROUTE_SEO } from '../utils/navigation';
import { Compass, Calendar, Shield, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface HomePageProps {
  onAttractionClick: (attr: Attraction) => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onAttractionClick }) => {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Category>('todos');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useSEO({
    title: ROUTE_SEO.home[lang].title,
    description: ROUTE_SEO.home[lang].description,
    canonicalPath: lang === 'es' ? '/' : `/${lang}/`
  });

  return (
    <div>
      {/* Main Interactive Hero */}
      <Hero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategorySelect={(cat) => setSelectedCategory(cat)}
        onOpenCalculator={() => navigate(getLocalizedRoute('consejos-manejo', lang))}
        onAttractionClick={onAttractionClick}
      />

      {/* Directory of Attractions */}
      <AttractionsDirectory
        attractions={ATTRACTIONS}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAttractionClick={onAttractionClick}
      />

      {/* Explore by Dedicated Sections (Hub Navigation) */}
      <section style={{ contentVisibility: 'auto', containIntrinsicSize: '0 450px' }} className="bg-stone-100/70 py-12 px-4 sm:px-6 lg:px-8 border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-teal-800">
              {lang === 'en' ? 'Complete Travel Hub' : lang === 'pt' ? 'Guia Completo' : 'Explora por Secciones'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#073B4C] font-['Outfit',sans-serif]">
              {lang === 'en' ? 'Plan Every Detail of Your Trip' : lang === 'pt' ? 'Planeje Cada Detalhe da Sua Viagem' : 'Todo lo que necesitas para tu viaje a Cancún'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Roadtrips Card */}
            <Link
              to={getLocalizedRoute('rutas', lang)}
              className="group bg-white p-6 rounded-2xl shadow-sm hover:shadow-md border border-stone-200 transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center group-hover:bg-teal-800 group-hover:text-white transition-colors">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 group-hover:text-teal-800 transition-colors">
                  {lang === 'en' ? 'Roadtrips & Driving Routes' : lang === 'pt' ? 'Roteiros de Carro' : 'Rutas y Roadtrips en Auto'}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {lang === 'en' 
                    ? 'Explore 3 scenic itineraries: Riviera Maya coast, Chichen Itza pyramid, and pristine Isla Blanca.'
                    : lang === 'pt'
                    ? 'Conheça 3 itinerários cênicos: Riviera Maya, Chichén Itzá e a paradisíaca Isla Blanca.'
                    : 'Conoce 3 circuitos recomendados: Riviera Maya costera, Chichén Itzá y la virgen Isla Blanca.'}
                </p>
              </div>
              <div className="pt-4 flex items-center gap-2 text-xs font-bold text-teal-800 group-hover:translate-x-1 transition-transform">
                <span>{lang === 'en' ? 'View all itineraries' : lang === 'pt' ? 'Ver itinerários' : 'Ver todos los circuitos'}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Events Card */}
            <Link
              to={getLocalizedRoute('eventos', lang)}
              className="group bg-white p-6 rounded-2xl shadow-sm hover:shadow-md border border-stone-200 transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center group-hover:bg-teal-800 group-hover:text-white transition-colors">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 group-hover:text-teal-800 transition-colors">
                  {lang === 'en' ? 'Events & Festivals' : lang === 'pt' ? 'Eventos e Festivais' : 'Eventos y Festivales'}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {lang === 'en'
                    ? 'Annual music festivals, Day of the Dead traditions at Xcaret, and vibrant Carnival celebrations.'
                    : lang === 'pt'
                    ? 'Festivais anuais de música, tradições do Dia dos Mortos em Xcaret e carnavais vibrantes.'
                    : 'Festivales de jazz, tradiciones de Vida y Muerte en Xcaret y desfiles del Carnaval de Cancún.'}
                </p>
              </div>
              <div className="pt-4 flex items-center gap-2 text-xs font-bold text-teal-800 group-hover:translate-x-1 transition-transform">
                <span>{lang === 'en' ? 'Explore calendar' : lang === 'pt' ? 'Ver calendário' : 'Consultar cartelera'}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Driving & Travel Tips Card */}
            <Link
              to={getLocalizedRoute('consejos-manejo', lang)}
              className="group bg-white p-6 rounded-2xl shadow-sm hover:shadow-md border border-stone-200 transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center group-hover:bg-teal-800 group-hover:text-white transition-colors">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 group-hover:text-teal-800 transition-colors">
                  {lang === 'en' ? 'Driving Guide & Calculator' : lang === 'pt' ? 'Guia de Direção e Economia' : 'Consejos de Manejo & Ahorro'}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {lang === 'en'
                    ? 'Safe driving rules, highway toll guide, emergency contacts, and the Car vs Taxi Savings Calculator.'
                    : lang === 'pt'
                    ? 'Regras de trânsito, pedágios, contatos de emergência e a Calculadora de Economia Carro vs Táxi.'
                    : 'Reglas viales, casetas de peaje, auxilio vial y la Calculadora comparativa de Ahorro Auto vs Taxi.'}
                </p>
              </div>
              <div className="pt-4 flex items-center gap-2 text-xs font-bold text-teal-800 group-hover:translate-x-1 transition-transform">
                <span>{lang === 'en' ? 'Open driving guide' : lang === 'pt' ? 'Abrir guia' : 'Abrir guía de manejo'}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Official Easy Way Rental Promo Banner */}
      <EasyWayPromo />
    </div>
  );
};
